/**
 * Simple mvc library
 * Controllers are created with upi.Module included in global.js
 */

import $ from '@modules/jquery-import';
import assign from 'object-assign';
import Events from './Events';

const Module = assign( {}, Events, {
    _delegates: [],

    // deprecated
    proxy ( func ) {
        // console.warn( `Deprecated Warning: Module.proxy` );
        if ( !func ) {
            return null;
        }
        const self = this;
        return function () {
            return func.apply( self, arguments );
        }
    },

    /**
     *
     * @param {String|Object} selector - jQuery or DOMElement for Module to mount to
     * @param {String} rest - selector (required if selector is instance of DOM/jQuery)
     * @returns {boolean}
     * @private
     */
    __init__ ( selector ) {

        $( document ).ready( this.proxy( function () {
            /* Called internally by Class.extend */
            // if jQuery nodeCollection provided use it otherwise instaniate a jQuery collection
            // this.nodeSelector can be specified as a property from the module
            this.el = (selector instanceof $) ? selector : $( selector || this.nodeSelector );

            if ( !this.el[0] ) {
                console.error( `Module::DOM element not found for selector "${selector}"` );
                return;
            }

            this.update_elements();
            this.delegate_events();
            // execute .ready callbacks
            this.empty_ready_list();
            this.initialized = true;
        } ) );

        return true;
    },

    empty_ready_list () {
        // if required element does not exist defer instatiation and destroy
        if ( !this.el[0] ) {
            this._destroy();
            return;
        }

        const ready_list = this.ready_list;

        while ( ready_list && ready_list.length ) {
            ready_list.shift().call( this );
        }
    },

    ready ( fn ) {
        const self = this;
        this.ready_list = this.ready_list || [];

        if ( typeof fn === 'function' ) {
            // if already initialized fire immediately
            if ( this.initialized ) {
                fn.call( this );
            }
            // push to callstack
            else {
                // callbacks pushed to the readylist are delegated to jQuery's DOMready method
                // to retain call order integrity
                this.ready_list.push( function () {
                    $( self.proxy( fn ) );
                } );
            }
        }
    },

    // load this module with instance variables of that points to it's child elements
    update_elements () {
        for ( let selector in this.elements ) {
            if ( this.elements.hasOwnProperty( selector ) ) {
                // cache this node under it's alias and attach it's actual selector as an expando
                let el = this.el.find( selector ),
                    alias = this.elements[selector];
                el._selector = selector;
                this[alias] = el;
            }
        }
    },

    delegate_events () {
        if ( !this.events ) {
            return;
        }
        // remove all existing delegates to prevent multiple event bindings
        this.undelegate_events();

        // split events/selectors at first space and capture matches
        // expects event[:namespace]: fn[, fn]  - eg. "change:banner": "changeBanner, updateControls"
        const event_splitter = /^(\w+:*\w+)\s*(.*)$/;

        for ( let key in this.events ) {
            if ( this.events.hasOwnProperty( key ) ) {
                const handlers = this.events[key].split( /,\s*/ ), // splits handler list names separated by comma
                    match = key.match( event_splitter ),
                    ev = match[1];

                let alias = match[2];
                // iterate over list of method names and bind to appropriate event
                for ( let i = 0; i < handlers.length; i++ ) {

                    const func = this.proxy( this[handlers[i]] );
                    if ( !alias ) {
                        this.el.on( ev, func );
                    }
                    else {
                        // if a node is cached under an aliased "selector", set it's stored selector instead
                        // else the matched alias is the qualified selector
                        let selector = (this[alias] && this[alias]._selector) ? this[alias]._selector : alias;
                        this._delegates.push( {ev, selector, func} );
                        this.el.on( ev, selector, func );
                    }
                }
            }
        }
    },

    /**
     * iterate over tracked delegate and unbind them from the current el context removing stored delegate once complete
     */
    undelegate_events () {
        while ( this._delegates.length ) {
            let {ev, selector, func} = this._delegates.shift();
            this.el.off( ev, selector, func );
        }
    },

    refresh ( doDelegateEvents = false ) {
        this.update_elements();
        if ( doDelegateEvents ) {
            this.delegate_events();
        }
    },

    destroy () {
        this._destroy();
    },

    _destroy () {
        // restore original state when destroy is called
        if ( this.elements ) {
            //de-reference any DOM nodes from elements array
            for ( let elm in this.elements ) {
                if ( this.elements.hasOwnProperty( elm ) ) {
                    this[elm] = null;
                }
            }
        }

        this.undelegate_events();
        this.el.trigger( 'destroy:module' );
    }
} );

export default Module;
